// @ts-nocheck
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { XGButton, XGModal } from 'xg-cl';
import { Box } from '@mui/material';
import * as PropTypes from 'prop-types';
import './UnsavedChangesModal.css';

/**
 * UnsavedChangesModal
 *
 * This component renders a modal that displays a message asking the user if they want to continue editing, discard changes, or save and close the current form data.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onAction - Function that will be called with one of the following string arguments: 'continue', 'discard', 'save'
 * @param {Object} [props.rest] - Additional props that will be passed to the underlying XGModal component
 *
 * @returns {ReactElement} - The rendered modal component
 */
const UnsavedChangesModal = ({ isOpen, isSubmitting, onAction, ...rest }) => {
  const { t } = useTranslation(['common']);

  return (
    <XGModal
      id={'unsaved-changes-modal'}
      allowClosing={false}
      data-testid={'unsaved-changes-modal'}
      actions={
        <Box className='unsaved-changes-modal__actions'>
          <XGButton
            text={t('continueEditing')}
            type='secondary'
            size='medium'
            variant='outlined'
            onClick={() => onAction('continue')}
            className='text-capitalize'
            id='continue-editing-button'
            disabled={isSubmitting}
          />

          <XGButton
            text={t('discardChanges')}
            type='secondary'
            size='medium'
            variant='outlined'
            onClick={() => onAction('discard')}
            className='text-capitalize'
            id='discard-changes-button'
            disabled={isSubmitting}
          />

          <Box className='relative-position'>
            <XGButton
              text={t('saveAndClose')}
              type='primary'
              size='medium'
              variant='contained'
              onClick={() => onAction('save')}
              className='text-capitalize'
              disabled={isSubmitting}
              id='save-and-close-button'
            />
            {isSubmitting && (
              <CircularProgress
                id='saving-progress-indicator'
                size={24}
                className='spinner spinner__primary'
              />
            )}
          </Box>
        </Box>
      }
      dismissable={false}
      open={isOpen}
      title={t('unsavedChangesTitle')}
      labelledby={t('unsavedChangesTitle')}
      maxWidth='lg'
      scroll='body'
      onClose={() => onAction('continue')}
      {...rest}
    >
      <Box className='unsaved-changes-modal__content'>
        <p className='text-sm-regular'>{t('common:unsavedChangesMessage')}</p>
      </Box>
    </XGModal>
  );
};

UnsavedChangesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool,
  onAction: PropTypes.func.isRequired
};

UnsavedChangesModal.defaultProps = {
  isOpen: false,
  isSubmitting: false,
  onAction: () => {}
};

export default UnsavedChangesModal;
